// Typed helpers for /api/calendar (consent, sources, availability rules, slot previews and the
// holds MeeMail made for a card). Nothing here writes to Google Calendar: holds and bookings are
// card actions that go through the approve strip and the server's gateway. URL builders are pure
// so they can be unit-tested; calendar ids travel as one encoded path segment.
import type {
  CalendarAccountView,
  CalendarHoldView,
  CalendarSourceView,
  SlotsPreviewRequest,
  SlotsPreviewResponse,
} from "@shared/api";
import type { AvailabilityRules } from "@shared/core";
import { api } from "./api";

const enc = encodeURIComponent;

export const calendarUrls = {
  accounts: () => "/api/calendar/accounts",
  account: (accountId: string) => `/api/calendar/accounts/${enc(accountId)}`,
  connect: (accountId: string) => `/api/calendar/accounts/${enc(accountId)}/connect`,
  sources: (accountId: string) => `/api/calendar/accounts/${enc(accountId)}/sources`,
  source: (accountId: string, calendarId: string) =>
    `/api/calendar/accounts/${enc(accountId)}/sources/${enc(calendarId)}`,
  rules: (accountId: string) => `/api/calendar/accounts/${enc(accountId)}/rules`,
  slots: () => "/api/calendar/slots",
  holds: (cardId: string) => `/api/calendar/cards/${enc(cardId)}/holds`,
};

export interface NewSource {
  calendarId: string;
  summary?: string;
  countsForAvailability?: boolean;
}

export const calendarApi = {
  accounts: (signal?: AbortSignal) =>
    api<CalendarAccountView[]>(calendarUrls.accounts(), { signal }),
  account: (accountId: string, signal?: AbortSignal) =>
    api<CalendarAccountView>(calendarUrls.account(accountId), { signal }),
  /** Google's consent page for the calendar scopes; the browser is handed over to it. */
  connect: (accountId: string) =>
    api<{ url: string }>(calendarUrls.connect(accountId), { method: "POST", body: {} }),
  sources: (accountId: string, signal?: AbortSignal) =>
    api<CalendarSourceView[]>(calendarUrls.sources(accountId), { signal }),
  addSource: (accountId: string, body: NewSource) =>
    api<CalendarSourceView[]>(calendarUrls.sources(accountId), { method: "POST", body }),
  setSourceCounts: (accountId: string, calendarId: string, countsForAvailability: boolean) =>
    api<CalendarSourceView[]>(calendarUrls.source(accountId, calendarId), {
      method: "PATCH",
      body: { countsForAvailability },
    }),
  removeSource: (accountId: string, calendarId: string) =>
    api<CalendarSourceView[]>(calendarUrls.source(accountId, calendarId), { method: "DELETE" }),
  rules: (accountId: string, signal?: AbortSignal) =>
    api<AvailabilityRules>(calendarUrls.rules(accountId), { signal }),
  saveRules: (accountId: string, rules: AvailabilityRules) =>
    api<AvailabilityRules>(calendarUrls.rules(accountId), { method: "PUT", body: rules }),
  /** Free, rule-compliant slots (read-only; also serves "Pick other times"). */
  slots: (body: SlotsPreviewRequest, signal?: AbortSignal) =>
    api<SlotsPreviewResponse>(calendarUrls.slots(), { method: "POST", body, signal }),
  holds: (cardId: string, signal?: AbortSignal) =>
    api<CalendarHoldView[]>(calendarUrls.holds(cardId), { signal }),
};
